import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * ProfileOptimizationRating
 * Muestra el porcentaje de optimización del perfil de Google Business Profile
 * Props:
 * - percentage: number (0-100) - porcentaje de optimización
 * - size: 'small' | 'medium' | 'large' - tamaño del componente
 * - showLabel: boolean - si mostrar la etiqueta "Profile Optimization"
 */
const ProfileOptimizationRating = ({
  percentage = 0,
  size = 'medium',
  showLabel = true
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  // Animación del porcentaje cuando el componente se monta o cambia el percentage
  useEffect(() => {
    const startValue = animatedPercentage;
    const endValue = percentage;
    const difference = endValue - startValue;

    // Si la diferencia es muy pequeña, no animar
    if (Math.abs(difference) < 5) {
      setAnimatedPercentage(percentage);
      return;
    }

    const duration = 600; // 0.6 segundos - más rápido
    const steps = 30; // 30 pasos para una animación más suave
    const increment = difference / steps;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const newValue = startValue + increment * currentStep;
      setAnimatedPercentage(Math.round(newValue));

      if (currentStep >= steps) {
        setAnimatedPercentage(endValue); // Asegurar que llegue al valor final exacto
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [percentage, animatedPercentage]);

  // Validar que el porcentaje esté entre 0 y 100
  const validPercentage = Math.min(Math.max(animatedPercentage, 0), 100);

  // Configurar tamaños según la prop size
  const sizeConfig = {
    small: {
      containerSize: 60,
      strokeWidth: 4,
      fontSize: 12,
      labelSize: 10
    },
    medium: {
      containerSize: 80,
      strokeWidth: 6,
      fontSize: 16,
      labelSize: 12
    },
    large: {
      containerSize: 100,
      strokeWidth: 8,
      fontSize: 24,
      labelSize: 11
    }
  };

  const config = sizeConfig[size];
  const radius = (config.containerSize - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset =
    circumference - (validPercentage / 100) * circumference;

  // Color único: usar siempre el azul primario del tema
  const getColor = () => 'hsl(var(--primary))';

  const color = getColor(validPercentage);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
      }}>
      <div
        style={{
          position: 'relative',
          width: config.containerSize,
          height: config.containerSize
        }}>
        {/* Círculo de fondo */}
        <svg
          width={config.containerSize}
          height={config.containerSize}
          style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={config.containerSize / 2}
            cy={config.containerSize / 2}
            r={radius}
            stroke="#e9ecef"
            strokeWidth={config.strokeWidth}
            fill="none"
          />
          {/* Círculo de progreso */}
          <circle
            cx={config.containerSize / 2}
            cy={config.containerSize / 2}
            r={radius}
            stroke={color}
            strokeWidth={config.strokeWidth}
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.1s ease-out'
            }}
          />
        </svg>
        {/* Texto del porcentaje y label dentro del círculo */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px'
          }}>
          <div
            style={{
              fontSize: config.fontSize,
              fontWeight: 'bold',
              color: color,
              lineHeight: 1,
              transition: 'color 0.3s ease'
            }}>
            {validPercentage}%
          </div>
          {showLabel && (
            <div
              style={{
                fontSize: config.labelSize,
                color: 'hsl(var(--muted-foreground))',
                fontWeight: 'normal',
                lineHeight: 1
              }}>
              Optimized
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ProfileOptimizationRating.propTypes = {
  percentage: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showLabel: PropTypes.bool
};

export default ProfileOptimizationRating;
